// L2 — referential validation (CREATIVE-MODEL-V1.md §13.1).
// Ids unique per namespace, every reference resolves to an entity of the
// right kind, the element containment graph is a forest, variants are
// selectable, components resolve to an exact registry version.

import { FAIL } from "../schema/dsl.js";
import type { AssetKind, CreativeDocument, Element, TextContent } from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { resolveComponent } from "../registry.js";
import { inLayout, type DocIndex } from "./context.js";

function duplicates<T>(
  sink: IssueSink,
  items: readonly T[] | undefined,
  key: (t: T) => string,
  base: string,
  field = "id"
) {
  const seen = new Set<string>();
  (items ?? []).forEach((item, i) => {
    const k = key(item);
    if (seen.has(k))
      sink.add("DUPLICATE_ID", `${base}[${String(i)}].${field}`, `Duplicate identifier "${k}".`, { id: k });
    seen.add(k);
  });
}

function textBinds(content: TextContent): string[] {
  return content.runs.flatMap((r) => ("bind" in r ? [r.bind.var] : []));
}

export interface ReferenceOptions {
  /** Distribution formats that must be selectable (CREATIVE-MODEL §4.2, RUNTIME-CONTRACT §3.3). */
  readonly requiredFormats?: readonly string[] | undefined;
}

export function validateReferences(index: DocIndex, sink: IssueSink, options: ReferenceOptions = {}): void {
  const doc: CreativeDocument = index.doc;

  // Unique ids per namespace.
  duplicates(sink, doc.layouts, (l) => l.id, "$.layouts");
  duplicates(sink, doc.scenes, (s) => s.id, "$.scenes");
  duplicates(sink, doc.elements, (e) => e.id, "$.elements");
  duplicates(sink, doc.assets, (a) => a.key, "$.assets", "key");
  duplicates(sink, doc.fonts, (f) => f.key, "$.fonts", "key");
  duplicates(sink, doc.links, (l) => l.id, "$.links");
  duplicates(sink, doc.variables, (v) => v.id, "$.variables");
  duplicates(sink, doc.timers, (t) => t.id, "$.timers");
  duplicates(sink, doc.rules, (r) => r.id, "$.rules");
  duplicates(sink, doc.events, (e) => e.name, "$.events", "name");
  duplicates(sink, doc.forms, (f) => f.id, "$.forms");
  doc.elements.forEach((el, i) => {
    duplicates(sink, el.animations, (t) => t.id, `$.elements[${String(i)}].animations`);
  });

  const asset = (key: string, path: string, kinds: readonly AssetKind[]) => {
    const found = index.assets.get(key);
    if (!found) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown asset "${key}".`, { kind: "asset", id: key });
      return;
    }
    if (!kinds.includes(found.value.kind))
      sink.add("ASSET_KIND_MISMATCH", path, `Asset "${key}" must be of kind ${kinds.join(" or ")}.`, {
        expected: kinds.join("|"),
        actual: found.value.kind,
      });
  };
  const ref = (map: ReadonlyMap<string, unknown>, id: string, path: string, kind: string) => {
    if (!map.has(id)) sink.add("UNKNOWN_REFERENCE", path, `Unknown ${kind} "${id}".`, { kind, id });
  };
  const numberVar = (id: string, path: string) => {
    const v = index.variables.get(id);
    if (!v) sink.add("UNKNOWN_REFERENCE", path, `Unknown variable "${id}".`, { kind: "variable", id });
    else if (v.value.type !== "number")
      sink.add("WRONG_REFERENCE_KIND", path, `Variable "${id}" must be a number.`, {
        expected: "number",
        actual: v.value.type,
      });
  };

  // Scenes and initial scene.
  ref(index.scenes, doc.initialSceneId, "$.initialSceneId", "scene");

  // Containment: every child is an element; one parent per element; no cycles.
  const parentCount = new Map<string, number>();
  const child = (id: string, path: string) => {
    if (!index.elements.has(id)) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown element "${id}".`, { kind: "element", id });
      return;
    }
    const n = (parentCount.get(id) ?? 0) + 1;
    parentCount.set(id, n);
    if (n > 1) sink.add("MULTIPLE_PARENTS", path, `Element "${id}" has more than one parent.`, { id });
  };
  doc.scenes.forEach((scene, si) => {
    const sp = `$.scenes[${String(si)}]`;
    scene.children.forEach((c, i) => {
      child(c, `${sp}.children[${String(i)}]`);
    });
    for (const [layoutId, ids] of Object.entries(scene.childrenByLayout ?? {})) {
      const lp = `${sp}.childrenByLayout.${layoutId}`;
      ref(index.layouts, layoutId, lp, "layout");
      const seen = new Set<string>();
      ids.forEach((c, i) => {
        if (!scene.children.includes(c))
          sink.add(
            "UNKNOWN_REFERENCE",
            `${lp}[${String(i)}]`,
            `Variant composition may only reorder or omit children of scene "${scene.id}".`,
            {
              kind: "element",
              id: c,
            }
          );
        if (seen.has(c)) sink.add("DUPLICATE_ID", `${lp}[${String(i)}]`, `Duplicate child "${c}".`, { id: c });
        seen.add(c);
      });
    }
    if (typeof scene.onTimelineEnd === "object")
      ref(index.scenes, scene.onTimelineEnd.goTo, `${sp}.onTimelineEnd.goTo`, "scene");
    if (scene.background?.kind === "image") asset(scene.background.asset, `${sp}.background.asset`, ["image"]);
  });
  doc.overlay?.below.forEach((c, i) => {
    child(c, `$.overlay.below[${String(i)}]`);
  });
  doc.overlay?.above.forEach((c, i) => {
    child(c, `$.overlay.above[${String(i)}]`);
  });

  doc.elements.forEach((el, ei) => {
    const p = `$.elements[${String(ei)}]`;
    if (el.type === "group")
      el.children.forEach((c, i) => {
        child(c, `${p}.children[${String(i)}]`);
      });
    validateElement(el, p);
  });

  // Group/slot cycles: DFS over ALL containment edges (not only the first parent).
  const childrenOf = (id: string): readonly string[] => {
    const el = index.elements.get(id)?.value;
    if (el?.type === "group") return el.children;
    if (el?.type === "component") return Object.values(el.slots ?? {}).flat();
    return [];
  };
  const color = new Map<string, "grey" | "black">();
  const reportedCycle = new Set<string>();
  const visitContainment = (id: string) => {
    color.set(id, "grey");
    for (const c of childrenOf(id)) {
      if (color.get(c) === "grey") {
        if (!reportedCycle.has(c)) {
          reportedCycle.add(c);
          sink.add(
            "PARENT_CYCLE",
            index.elements.get(c)?.path ?? "$.elements",
            `Element "${c}" is contained in itself.`,
            { id: c }
          );
        }
      } else if (!color.has(c) && index.elements.has(c)) visitContainment(c);
    }
    color.set(id, "black");
  };
  for (const id of [...index.elements.keys()].sort()) if (!color.has(id)) visitContainment(id);
  for (const [id, entry] of index.elements) {
    if (!parentCount.has(id))
      sink.warn("ORPHAN_ELEMENT", entry.path, `Element "${id}" is not placed in any scene, group, slot or overlay.`, {
        id,
      });
  }

  // Layouts: unique format keys, coherent responsive ranges, overrides.
  const formatOwner = new Map<string, string>();
  doc.layouts.forEach((layout, li) => {
    const lp = `$.layouts[${String(li)}]`;
    layout.formatKeys.forEach((key, ki) => {
      const owner = formatOwner.get(key);
      if (owner !== undefined && owner !== layout.id)
        sink.add(
          "DUPLICATE_FORMAT_KEY",
          `${lp}.formatKeys[${String(ki)}]`,
          `Format "${key}" is already served by layout "${owner}".`,
          {
            format: key,
            layout: owner,
          }
        );
      else formatOwner.set(key, layout.id);
    });
    if (layout.sizing.mode === "responsive") {
      const { range, design, aspect } = layout.sizing;
      if (range.minWidth > range.maxWidth || range.minHeight > range.maxHeight)
        sink.add("INVALID_VALUE", `${lp}.sizing.range`, "Range minimum exceeds maximum.", {});
      if (
        design.width < range.minWidth ||
        design.width > range.maxWidth ||
        design.height < range.minHeight ||
        design.height > range.maxHeight
      )
        sink.add("INVALID_VALUE", `${lp}.sizing.design`, "Design size must lie inside the responsive range.", {});
      if (aspect && aspect.min > aspect.max)
        sink.add("INVALID_VALUE", `${lp}.sizing.aspect`, "Aspect minimum exceeds maximum.", {});
    }
    for (const [elementId, override] of Object.entries(layout.overrides ?? {})) {
      const op = `${lp}.overrides.${elementId}`;
      const el = index.elements.get(elementId)?.value;
      if (!el) {
        sink.add("UNKNOWN_REFERENCE", op, `Unknown element "${elementId}".`, { kind: "element", id: elementId });
        continue;
      }
      if (!inLayout(index, elementId, layout.id))
        sink.warn(
          "NOT_IN_LAYOUT",
          op,
          `Element "${elementId}" does not exist in layout "${layout.id}"; override has no effect.`,
          {}
        );
      const textual = el.type === "text" || el.type === "button";
      if (override.typography && !textual)
        sink.add("OVERRIDE_NOT_APPLICABLE", `${op}.typography`, `Typography does not apply to ${el.type} elements.`, {
          type: el.type,
        });
      if (override.content && !textual)
        sink.add("OVERRIDE_NOT_APPLICABLE", `${op}.content`, `Text content does not apply to ${el.type} elements.`, {
          type: el.type,
        });
      if (override.fit && el.type !== "image" && el.type !== "video")
        sink.add("OVERRIDE_NOT_APPLICABLE", `${op}.fit`, `Fit does not apply to ${el.type} elements.`, {
          type: el.type,
        });
      if (override.typography?.font !== undefined)
        ref(index.fonts, override.typography.font, `${op}.typography.font`, "font");
      if (override.content)
        textBinds(override.content).forEach((v) => {
          numberVar(v, `${op}.content`);
        });
      for (const [state, patch] of Object.entries(override.states ?? {}))
        if (patch.asset !== undefined) asset(patch.asset, `${op}.states.${state}.asset`, ["image"]);
      override.animations?.forEach((track, ti) => {
        if (override.animations?.findIndex((t) => t.id === track.id) !== ti)
          sink.add("DUPLICATE_ID", `${op}.animations[${String(ti)}].id`, `Duplicate identifier "${track.id}".`, {
            id: track.id,
          });
      });
    }
  });
  for (const format of options.requiredFormats ?? []) {
    if (!formatOwner.has(format))
      sink.add("FORMAT_NOT_AVAILABLE", "$.layouts", `No layout variant serves format "${format}".`, { format });
  }

  // Fonts and links.
  doc.fonts?.forEach((font, i) => {
    asset(font.asset, `$.fonts[${String(i)}].asset`, ["font"]);
  });

  // Fallback.
  const fp = "$.fallback";
  if (doc.fallback) ref(index.links, doc.fallback.linkId, `${fp}.linkId`, "link");
  for (const [layoutId, entry] of Object.entries(doc.fallback?.perLayout ?? {})) {
    const ep = `${fp}.perLayout.${layoutId}`;
    const layout = index.layouts.get(layoutId)?.value;
    if (!layout) sink.add("UNKNOWN_REFERENCE", ep, `Unknown layout "${layoutId}".`, { kind: "layout", id: layoutId });
    const a = index.assets.get(entry.asset)?.value;
    if (!a)
      sink.add("UNKNOWN_REFERENCE", `${ep}.asset`, `Unknown asset "${entry.asset}".`, {
        kind: "asset",
        id: entry.asset,
      });
    else if (a.kind !== "image" || !["image/png", "image/jpeg", "image/webp"].includes(a.mime))
      sink.add("FALLBACK_INVALID", `${ep}.asset`, "Fallback must be a static PNG, JPEG or WebP image.", {
        mime: a.mime,
      });
    else if (layout?.sizing.mode === "fixed" && (a.width !== layout.sizing.width || a.height !== layout.sizing.height))
      sink.add("FALLBACK_INVALID", `${ep}.asset`, "Fallback dimensions must equal the fixed layout size.", {
        expected: `${String(layout.sizing.width)}x${String(layout.sizing.height)}`,
        actual: `${String(a.width)}x${String(a.height)}`,
      });
    else if (layout?.sizing.mode === "responsive" && a.width !== undefined && a.height !== undefined) {
      const ratio = a.width / a.height;
      const design = layout.sizing.design.width / layout.sizing.design.height;
      if (Math.abs(ratio - design) / design > 0.01)
        sink.add("FALLBACK_INVALID", `${ep}.asset`, "Fallback aspect ratio must match the responsive design size.", {});
    }
    if (entry.source === "generated" && !entry.generatedFrom)
      sink.add(
        "REQUIRED",
        `${ep}.generatedFrom`,
        "Generated fallbacks record the scene and instant they were rendered from.",
        {}
      );
    if (entry.generatedFrom) {
      const scene = index.scenes.get(entry.generatedFrom.sceneId)?.value;
      if (!scene)
        sink.add(
          "UNKNOWN_REFERENCE",
          `${ep}.generatedFrom.sceneId`,
          `Unknown scene "${entry.generatedFrom.sceneId}".`,
          {
            kind: "scene",
            id: entry.generatedFrom.sceneId,
          }
        );
      else if (scene.duration !== null && entry.generatedFrom.atMs > scene.duration)
        sink.add("TIMING_INVALID", `${ep}.generatedFrom.atMs`, "Instant is beyond the scene duration.", {
          max: scene.duration,
        });
    }
  }

  // Game module (7.6F): every id it names exists (and score/lives are numbers). Roles,
  // pairs, drops and win/results coherence are checked by the game layer (L3).
  const game = doc.game;
  if (game) {
    game.scenes.forEach((sc, i) => {
      ref(index.scenes, sc, `$.game.scenes[${String(i)}]`, "scene");
    });
    if (game.score !== undefined) numberVar(game.score, "$.game.score");
    if (game.lives !== undefined) numberVar(game.lives, "$.game.lives");
    if (game.timer !== undefined) ref(index.timers, game.timer, "$.game.timer", "timer");
    for (const key of ["win", "lose", "timeout"] as const) {
      const sc = game.results[key];
      if (sc !== undefined) ref(index.scenes, sc, `$.game.results.${key}`, "scene");
    }
    game.targets?.forEach((t, i) => {
      const tp = `$.game.targets[${String(i)}]`;
      ref(index.elements, t.element, `${tp}.element`, "element");
      if (t.relocate !== undefined) ref(index.elements, t.relocate, `${tp}.relocate`, "element");
    });
    game.pairs?.cards.forEach((pair, i) => {
      pair.forEach((c, j) => {
        ref(index.elements, c, `$.game.pairs.cards[${String(i)}][${String(j)}]`, "element");
      });
    });
    game.drops?.items.forEach((it, i) => {
      ref(index.elements, it, `$.game.drops.items[${String(i)}]`, "element");
    });
    game.drops?.zones.forEach((z, i) => {
      ref(index.elements, z, `$.game.drops.zones[${String(i)}]`, "element");
    });
    const runner = game.runner;
    if (runner) {
      const rp = "$.game.runner";
      ref(index.elements, runner.player.element, `${rp}.player.element`, "element");
      runner.layers?.forEach((l, i) => {
        ref(index.elements, l.element, `${rp}.layers[${String(i)}].element`, "element");
      });
      runner.segments.forEach((sg, i) => {
        sg.show.forEach((id, j) => {
          ref(index.elements, id, `${rp}.segments[${String(i)}].show[${String(j)}]`, "element");
        });
      });
      runner.items?.forEach((it, i) => {
        ref(index.elements, it.element, `${rp}.items[${String(i)}].element`, "element");
      });
      runner.spawns?.forEach((sp, i) => {
        sp.templates.forEach((id, j) => {
          ref(index.elements, id, `${rp}.spawns[${String(i)}].templates[${String(j)}]`, "element");
        });
      });
      if (runner.controls?.jump !== undefined)
        ref(index.elements, runner.controls.jump, `${rp}.controls.jump`, "element");
      if (runner.controls?.duck !== undefined)
        ref(index.elements, runner.controls.duck, `${rp}.controls.duck`, "element");
      if (runner.energy) numberVar(runner.energy.var, `${rp}.energy.var`);
      for (const key of Object.keys(runner.speedByLayout ?? {}))
        ref(index.layouts, key, `${rp}.speedByLayout.${key}`, "layout");
    }
  }

  // Forms and survey option images.
  doc.survey?.questions.forEach((q, qi) => {
    if (q.kind === "scale") return;
    q.options.forEach((o, oi) => {
      if (o.image !== undefined)
        asset(o.image, `$.survey.questions[${String(qi)}].options[${String(oi)}].image`, ["image"]);
    });
  });

  function validateElement(el: Element, p: string) {
    el.onlyIn?.forEach((l, i) => {
      ref(index.layouts, l, `${p}.onlyIn[${String(i)}]`, "layout");
    });
    if (el.initialState !== undefined && !Object.hasOwn(el.states ?? {}, el.initialState))
      sink.add("UNKNOWN_REFERENCE", `${p}.initialState`, `Unknown state "${el.initialState}".`, {
        kind: "state",
        id: el.initialState,
      });
    for (const [state, patch] of Object.entries(el.states ?? {})) {
      if (patch.asset !== undefined) {
        if (el.type !== "image")
          sink.add(
            "OVERRIDE_NOT_APPLICABLE",
            `${p}.states.${state}.asset`,
            "Asset swaps apply to image elements only.",
            {}
          );
        else asset(patch.asset, `${p}.states.${state}.asset`, ["image"]);
      }
      if (patch.text !== undefined && el.type !== "text" && el.type !== "button")
        sink.add(
          "OVERRIDE_NOT_APPLICABLE",
          `${p}.states.${state}.text`,
          "Text patches apply to text and button elements only.",
          {}
        );
    }
    switch (el.type) {
      case "text":
      case "button": {
        if (el.typography.font !== undefined) ref(index.fonts, el.typography.font, `${p}.typography.font`, "font");
        textBinds(el.type === "text" ? el.content : el.label).forEach((v) => {
          numberVar(v, `${p}.${el.type === "text" ? "content" : "label"}`);
        });
        break;
      }
      case "image":
        asset(el.asset, `${p}.asset`, ["image"]);
        break;
      case "video":
        asset(el.asset, `${p}.asset`, ["video"]);
        if (el.poster !== undefined) asset(el.poster, `${p}.poster`, ["image"]);
        if (el.captions !== undefined) asset(el.captions, `${p}.captions`, ["captions"]);
        break;
      case "audio":
        asset(el.asset, `${p}.asset`, ["audio"]);
        break;
      case "shape":
        if ((el.shape === "polygon" || el.shape === "line") && !el.points)
          sink.add("REQUIRED", `${p}.points`, `${el.shape} shapes declare points.`, {});
        if (el.shape === "path" && el.path === undefined)
          sink.add("REQUIRED", `${p}.path`, "Path shapes declare a path.", {});
        break;
      case "field": {
        const fields = index.fields.get(el.form);
        if (!fields)
          sink.add("UNKNOWN_REFERENCE", `${p}.form`, `Unknown form "${el.form}".`, { kind: "form", id: el.form });
        else if (!fields.has(el.field))
          sink.add("UNKNOWN_REFERENCE", `${p}.field`, `Unknown field "${el.field}" in form "${el.form}".`, {
            kind: "field",
            id: el.field,
          });
        break;
      }
      case "component":
        validateComponent(el, p);
        break;
      case "group":
      case "hotspot":
        break;
    }
  }

  function validateComponent(el: Extract<Element, { type: "component" }>, p: string) {
    const resolved = resolveComponent(el.component);
    if (!resolved.ok) {
      sink.add(
        resolved.reason === "UNKNOWN_VERSION" ? "INVALID_COMPONENT_VERSION" : "INVALID_COMPONENT",
        `${p}.component`,
        resolved.reason === "UNKNOWN_VERSION" ? "This component version is not registered." : "Unknown component.",
        { component: el.component }
      );
      return;
    }
    const def = resolved.definition;
    const props = def.props.parse(el.props, `${p}.props`, sink);
    if (props === FAIL) return;
    const propRecord = props as Readonly<Record<string, unknown>>;
    for (const [prop, kind] of Object.entries(def.refs)) {
      const value = propRecord[prop];
      if (typeof value !== "string") continue;
      const pp = `${p}.props.${prop}`;
      if (kind === "survey") {
        if (doc.survey?.id !== value)
          sink.add("UNKNOWN_REFERENCE", pp, `Unknown survey "${value}".`, { kind, id: value });
      } else if (kind === "timer") ref(index.timers, value, pp, "timer");
      else numberVar(value, pp);
    }
    if (el.component === "ia.progress@1") {
      const source = propRecord.source as string;
      if (propRecord[source] === undefined)
        sink.add(
          "COMPONENT_PROPS_INVALID",
          `${p}.props.${source}`,
          `Progress with source "${source}" names the ${source} it follows.`,
          {}
        );
    }
    const slots = el.slots ?? {};
    for (const name of Object.keys(slots))
      if (!Object.hasOwn(def.slots, name))
        sink.add("SLOT_INVALID", `${p}.slots.${name}`, `Component has no slot "${name}".`, {});
    for (const [name, spec] of Object.entries(def.slots)) {
      const ids = slots[name] ?? [];
      if (ids.length < spec.min || ids.length > spec.max)
        sink.add(
          "SLOT_INVALID",
          `${p}.slots.${name}`,
          `Slot "${name}" takes ${String(spec.min)}..${String(spec.max)} elements.`,
          {
            min: spec.min,
            max: spec.max,
            actual: ids.length,
          }
        );
      ids.forEach((c, i) => {
        const sp = `${p}.slots.${name}[${String(i)}]`;
        child(c, sp);
        const target = index.elements.get(c)?.value;
        if (target && !spec.accepts.includes(target.type))
          sink.add("SLOT_INVALID", sp, `Slot "${name}" accepts ${spec.accepts.join("|")} elements.`, {
            actual: target.type,
          });
      });
    }
  }
}
